/**
 * Mapping modèle + variable → nom de couche WMS complet
 *
 * Chaque modèle a un préfixe de région différent :
 *   RDPS  → RDPS_10km_AirTemp_2m
 *   GDPS  → GDPS.ETA_TT
 *   HRDPS → HRDPS.CONTINENTAL_TT
 *   RAQDPS → RAQDPS.EATM_PM2.5
 */

// Préfixe de région par modèle
const REGION_PREFIX = {
  RDPS:    '_10km_',
  GDPS:    '.ETA_',
  HRDPS:   '.CONTINENTAL_',
  RAQDPS:  '.',   // ex: RAQDPS.EATM_PM2.5 (pas de préfixe de région)
};

const RDPS_vars = {
  TT: "AirTemp_2m",
  ES: "DewPointDepression_2m",
  PR: "Precip-Accum",
  RN: "Rain-Accum",
  SN: "Snow-Accum",
  FR: "FreezingRain-Accum",
  WSPD: "WindSpeed_10m",
  WD: "WindDir_10m",
  WGE: "WindGust_10m",
  NT: "TotalCloudCover",
  N4: "DownwardShortwaveRadiationFlux-Accum"
};

/**
 * Retourne le nom de couche WMS complet pour un modèle et une variable.
 *
 * @param {string} model    - ex: 'HRDPS', 'RDPS', 'GDPS', 'RAQDPS'
 * @param {string} variable - ex: 'TT', 'ES', 'WSPD', 'EATM_PM2.5'
 * @returns {string}        - ex: 'HRDPS.CONTINENTAL_TT'
 * @throws {Error} si le modèle n'est pas connu
 */
export function layerName(model, variable) {
  const m = model.toUpperCase();
  if (!(m in REGION_PREFIX)) {
    throw new Error(`Modèle inconnu : "${model}". Modèles supportés : ${Object.keys(REGION_PREFIX).join(', ')}`);
  }
  let varname = variable
  if(model == "RDPS"){
    varname = RDPS_vars[variable]
  }
  if (varname === undefined) {
    throw new Error(`Variable non défénie pour "${model}" : "${variable}"`);
  }
  return `${m}${REGION_PREFIX[m]}${varname}`;
}

/**
 * Retourne la liste des noms de couches WMS pour un modèle et une liste de variables.
 *
 * @param {string}   model     - ex: 'HRDPS'
 * @param {string[]} variables - ex: ['TT', 'ES']
 * @returns {string[]}         - ex: ['HRDPS.CONTINENTAL_TT', 'HRDPS.CONTINENTAL_ES']
 */
export function layerNames(model, variables) {
  return variables.map(v => layerName(model, v));
}

/**
 * Style WMS explicite par variable.
 *
 * On fixe le style pour chaque variable afin d'être indépendant des changements
 * de style par défaut côté SMC/GeoMet.
 *
 * Les valeurs correspondent aux styles par défaut observés le 2026-04-10,
 * sauf WGE : le défaut (GUST_INTERV_MS2KTS) masque les rafales < 15 kt ;
 * on utilise GUST_MS2KMH qui couvre toutes les valeurs à partir de 0.
 *
 * Pour GZ, HRDPS utilise GEOPOTENTIALHEIGHT-LINEAR tandis que RDPS/GDPS
 * utilisent GPHEIGHT — voir MODEL_VARIABLE_STYLE pour l'override.
 *
 * Pour mettre à jour cette liste : make list-styles (dans wms-forecast-client)
 */
const VARIABLE_STYLE = {
  ES:           'DEWPOINTDEP',
  FR:           'CAPA24-LINEAR',
  N4:           'SHORTWAVEFLUX-LINEAR',
  NT:           'CLOUD',
  P0:           'PRESSURE',
  PR:           'CAPA24-LINEAR',
  RN:           'CAPA24-LINEAR',
  SN:           'Snow-Accum_0to100cm_BlueRed',
  TT:           'TEMPERATURE-LINEAR',
  WD:           'WDIR6-LINEAR',
  WGE:          'GUST_MS2KMH',
  WSPD:         'WINDSPEEDKNOTS-LINEAR',
  'EATM_PM2.5': 'PM2.5_EAtm_1e-7to2e-4kgm2',
  'SFC_PM2.5':  'PM2.5_0to100ugm3_Dis',
};

/**
 * Overrides de style par modèle, pour les variables dont le style par défaut
 * diffère selon le modèle.
 */
const MODEL_VARIABLE_STYLE = {};

/**
 * Retourne le style WMS à utiliser pour une variable et un modèle donnés,
 * ou '' si aucun style n'est défini.
 *
 * @param {string} variable - ex: 'WGE', 'GZ'
 * @param {string} [model=''] - ex: 'HRDPS', 'RDPS'
 * @returns {string}
 */
export function styleForVariable(variable, model = '') {
  return MODEL_VARIABLE_STYLE[model]?.[variable] ?? VARIABLE_STYLE[variable] ?? '';
}

/**
 * Liste des modèles supportés.
 * @returns {string[]}
 */
export function supportedModels() {
  return Object.keys(REGION_PREFIX);
}

/**
 * Couche WMS de référence par modèle, utilisée pour :
 *   - GetCapabilities (obtenir le dateo fiable)
 *   - GetFeatureInfo à P0 (obtenir le point de grille)
 *
 * On évite TT car les variables accumulées (PR, SN, RN…) ont un dateo
 * décalé d'une heure dans les capabilities — P0 n'a pas ce problème.
 * RAQDPS n'a pas de champ P0 ; on utilise SFC_PM2.5 à la place.
 */
const META_LAYER = {
  HRDPS:  'HRDPS.CONTINENTAL_P0',
  RDPS:   'RDPS_10km_Pressure',
  GDPS:   'GDPS.ETA_P0',
  RAQDPS: 'RAQDPS.SFC_PM2.5',
};

/**
 * Retourne la couche WMS de référence pour un modèle donné.
 *
 * @param {string} model - ex: 'HRDPS', 'RDPS', 'GDPS', 'RAQDPS'
 * @returns {string}     - ex: 'HRDPS.CONTINENTAL_P0'
 * @throws {Error} si le modèle n'est pas supporté
 */
export function metaLayer(model) {
  const m = model.toUpperCase();
  const layer = META_LAYER[m];
  if (!layer) {
    throw new Error(
      `Modèle inconnu : "${model}". Modèles supportés : ${Object.keys(META_LAYER).join(', ')}`
    );
  }
  return layer;
}
